/** Species-specific icons (Phosphor, MIT licence). Falls back to a generic paw for unknown species. */

import { PawPrint } from 'lucide-react'
import type { SVGProps } from 'react'

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number | string
  /** Stroke width in Lucide units (24-viewBox scale). Default: 2. */
  strokeWidth?: number | string
  /** When true, stroke width stays constant regardless of size. */
  absoluteStrokeWidth?: boolean
}

/** Scales a Lucide 24-viewBox strokeWidth to the icon's 256 viewBox (and corrects for size when absolute). */
function resolveStrokeWidth(
  strokeWidth: number | string,
  size: number | string,
  options?: { absolute?: boolean }
): number {
  const sw = Number(strokeWidth)
  const sz = Number(size)
  const scaled = sw * (256 / 24)
  return options?.absolute ? scaled * (24 / sz) : scaled
}

function DogIcon({
  size = 24,
  color = 'currentColor',
  strokeWidth = 2,
  absoluteStrokeWidth = false,
  ...props
}: IconProps) {
  const sw = resolveStrokeWidth(strokeWidth, size, { absolute: absoluteStrokeWidth })
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      width={size}
      height={size}
      color={color}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={sw}
      aria-hidden="true"
      {...props}
    >
      <line x1="128" y1="192" x2="128" y2="216" />
      <polyline points="144 176 128 192 112 176" />
      <line x1="104" y1="48" x2="152" y2="48" />
      <path d="M104,48,50.37,32.24a8,8,0,0,0-9.8,6.29l-16.42,88c-1.54,8.23,9,13,14.16,6.42Z" />
      <path d="M152,48l53.63-15.76a8,8,0,0,1,9.8,6.29l16.42,88c1.54,8.23-9,13-14.16,6.42Z" />
      <circle cx="92" cy="140" r="12" fill="currentColor" stroke="none" />
      <circle cx="164" cy="140" r="12" fill="currentColor" stroke="none" />
      <path d="M208,120.38V184a32,32,0,0,1-32,32H80a32,32,0,0,1-32-32V120.38" />
    </svg>
  )
}

function CatIcon({
  size = 24,
  color = 'currentColor',
  strokeWidth = 2,
  absoluteStrokeWidth = false,
  ...props
}: IconProps) {
  const sw = resolveStrokeWidth(strokeWidth, size, { absolute: absoluteStrokeWidth })
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      width={size}
      height={size}
      color={color}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={sw}
      aria-hidden="true"
      {...props}
    >
      <line x1="128" y1="192" x2="128" y2="224" />
      <circle cx="84" cy="140" r="12" fill="currentColor" stroke="none" />
      <circle cx="172" cy="140" r="12" fill="currentColor" stroke="none" />
      <line x1="128" y1="48" x2="128" y2="88" />
      <polyline points="144 176 128 192 112 176" />
      <line x1="96" y1="53.01" x2="96" y2="88" />
      <line x1="160" y1="53.01" x2="160" y2="88" />
      <path d="M67.6,67.6h0a102.87,102.87,0,0,1,120.8,0h0l21.94-25.24A8,8,0,0,1,224,48v88c0,48.6-43,88-96,88s-96-39.4-96-88V48a8,8,0,0,1,13.66-5.66Z" />
    </svg>
  )
}

/** Renders a species-appropriate icon based on the species string. */
export function SpeciesIcon({ species, className, ...props }: IconProps & { species: string }) {
  switch (species.toLowerCase()) {
    case 'cat':
      return <CatIcon className={className} {...props} />
    case 'dog':
      return <DogIcon className={className} {...props} />
    default:
      return <PawPrint className={className} {...props} />
  }
}
